import Box from '@mui/material/Box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import { checkPrivileges } from 'Helpers/CheckPrivileges';
import Loader from 'layout/Loader';
import BackendPaginationTable from 'common/Table/TableWithPagination';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid, Typography } from '@mui/material';
import StyledInput from 'common/SearchField/Search';
import { useGetInspectionFormItemService } from 'subModule/src/services/inspectionformitem/useInspectionFormItem';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ChangeStatusInspectionFormItemResponse, InspectionFormItemResponse } from 'subModule/src/services/inspectionformitem/interface';
import FormDialog from 'common/Modal/Modal';
import { InspectionFormItemProfileService, InspectionFormProfileService } from 'subModule/src/core/services';
import { DeleteData } from 'subModule/src/type';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import { ReactComponent as Inspection  } from "assets/SideBarIcons/inspection.svg";
import InspectionFormTabs from './InspectionFormTabs';
// import ComponentSubheader from 'common/ComponentHeaderTwo/ComponentSubheader';
import type { BreadcrumbItem } from 'Helpers/generalBreadcrumbs';

export default function InspectionFormItemListing() {
    const navigate = useNavigate();
    const params = useParams();
    const formId = params?.id;
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<any>('');
    const [search, setSearch] = useState('');
    const [openDeleteModal, setDeleteModal] = useState(false);
    const [row, setRow] = useState<InspectionFormItemResponse>();
    const [statusModal, setStatusModal] = useState(false);
    const [formTitle, setFormTitle] = useState('');
    const { data: tableData, isFetching, refetch } = searchTerm ? useGetInspectionFormItemService({ pageNumber: pageNo, size: pageSize, title: search }, +(params as any)?.id) : useGetInspectionFormItemService({ pageNumber: pageNo, size: pageSize }, +(params as any)?.id);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    useEffect(() => {
        if (!formId) return;
        let active = true;
        InspectionFormProfileService.getInspectionFormById(+formId)
            .then((res: any) => {
                if (!active) return;
                const name = String(
                    res?.data?.title ||
                        res?.data?.secondaryTitle ||
                        res?.title ||
                        '',
                ).trim();
                if (name) setFormTitle(name);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [formId]);

    const breadcrumbItems: BreadcrumbItem[] = useMemo(
        () => [
            { labelKey: 'general' },
            { labelKey: 'inspectionForms', href: '/inspection-forms' },
            {
                labelKey: 'title',
                ...(formTitle ? { label: formTitle } : {}),
                href: formId ? `/inspection-form/${formId}` : undefined,
            },
            { labelKey: 'inspectionFormItems' },
        ],
        [formId, formTitle],
    );

    const fetchData = useCallback(() => {
        refetch().then((res: any) => {
            if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        });
    }, []);
    useEffect(() => {
        fetchData();
    }, [pageNo, pageSize, searchTerm]);
    const handleChangePage = (pageNo: number) => {
        setPageNo(pageNo + 1);
    }
    const handleChangeRows = (pageSize: number) => {
        setPageSize(pageSize);
        setPageNo(1);
    }
    const onEdit = (row: any) => navigate(`/inspection-form-item/${params?.id}/edit/` + row.id);
    const showRowView = (row: any) => navigate(`/inspection-form-item/${params?.id}/edit/` + row.id);
    const onDelete = (row: any) => {
        setDeleteModal(true);
        setRow(row);
    }
    const clearSearch = () => {
        setSearch('');
        setSearchTerm('');
        if (searchTerm) { handleSearch(''); }
    }
    const handleSearch = (search: string) => {
        setSearchTerm(search);
        setPageNo(1);
        setPageSize(pageSize);
    };
    const handleSearchClick = () => {
        const trimmedSearch = search.trim();
        if (trimmedSearch !== '') {
            handleSearch(trimmedSearch);
        }
    };
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value.trim());
        if (!event.target.value.trim()) handleSearch('');
    };
    const onChangeStatus = (row: any) => {
        setRow(row);
        setStatusModal(true);
    }
    const actionslist = [
        { label: "edit", onClick: onEdit, icon: <EditIcon /> },
        { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
    ];
    const adjustActions = (actions: any[]) => {
        return checkPrivileges('ROLE_INSPECTIONFORMITEM_UPDATE') 
          ? (checkPrivileges('ROLE_INSPECTIONFORMITEM_ARCHIVE') 
              ? actions 
              : actions.filter(action => action.label !== 'delete')) 
          : (checkPrivileges('ROLE_INSPECTIONFORMITEM_ARCHIVE') 
              ? actions.filter(action => action.label !== 'edit') 
              : []);
    }

    return (
        <Box>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={checkPrivileges("ROLE_INSPECTIONFORMITEM_CREATE")}
                onClick={() => navigate(`/inspection-forms`)}
                onSubmit={() => navigate(`/inspection-form-item/${params?.id}/create`)}
                heading={t('inspectionForms')}
                icon={<Inspection height={28} width={28} />}
                btnText={"back"}
                btnType={"back"}
                showCreateBtn={false}
                saveBtnText={"create"}
                isBtnEnabled={true}
                breadcrumbItems={breadcrumbItems}
            />
            <Box className={`flex`}>
                <InspectionFormTabs params={params} selectedIndex={2} />
                <div className='w-[100%] mt-[10px]'>
                    {/* <ComponentSubheader
                        showBtn={true}
                        showSaveBtn={false}
                        onClick={() => navigate(`/inspection-form-item/${params?.id}/create`)}
                        heading={t('inspectionFormItems')}
                        icon={<Inspection height={28} width={28} />}
                        btnText={"create"}
                        btnType={"create"}
                        showCreateBtn={checkPrivileges("ROLE_INSPECTIONFORMITEM_CREATE")}
                    /> */}
                    {(isFetching || loading) ? <Loader /> :
                        <div id="mainListingTable">
                            <BackendPaginationTable
                                columns={[
                                    { id: 'title', label: 'title', numeric: false },
                                    ...(checkPrivileges("ROLE_INSPECTIONFORMITEM_ACTIVE") ? [{ id: 'active', label: 'status', numeric: false }] : []),
                                    ...(adjustActions(actionslist).length ? [{ id: 'actions', label: 'actions', numeric: false }] : []),
                                ]}
                                data={tableData?.pages[0]?.data?.content || []}
                                pageNo={pageNo}
                                pageSize={pageSize}
                                tableData={tableData?.pages[0]?.data || {}}
                                showCheckbox={false}
                                showActions={true}
                                enableSorting={true}
                                showHeader={true}
                                showSearch={true}
                                showFilter={true}
                                showCreateButton={true}
                                actions={adjustActions(actionslist)}
                                handleChangePage={handleChangePage}
                                handleChangeRows={handleChangeRows}
                                onChangeStatus={onChangeStatus}
                                rowClick={checkPrivileges("ROLE_INSPECTIONFORMITEM_UPDATE")}
                                showRowView={showRowView}
                                loadingSearch={false}
                                headerComponent={<Grid container spacing={2}>
                                    <Grid item xs={12} md={7} lg={5}>
                                        <StyledInput
                                            fullWidth
                                            placeholder={t("title")}
                                            handleSearchClick={handleSearchClick}
                                            handleChange={handleInputChange}
                                            value={search}
                                            clearSearch={clearSearch}
                                        />
                                    </Grid>
                                </Grid>}
                            />
                        </div>
                    }
                </div>
            </Box>
            {openDeleteModal &&
                <FormDialog
                    open={openDeleteModal}
                    onClose={() => setDeleteModal(false)}
                    title={t('delete') + ' ' + t('inspectionFormItem')}
                    // icon={<div><Home/></div>}
                    content={<Grid container>
                        <Grid item xs={12}>
                            <Typography>{t("Are you sure you want to delete?")}</Typography>
                        </Grid>
                    </Grid>}
                    saveFunction={() => {
                        setLoading(true);
                        setDeleteModal(false);
                        InspectionFormItemProfileService.deleteInspectionFormItem((row as any).id).then((response: DeleteData) => {
                            if (response.success) {
                                if (tableData?.pages[0]?.data?.content?.length === 1 && (pageNo > 1)) setPageNo(1);
                                else refetch();
                                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('inspectionFormItem') + ' ' + t('deleted') + ' ' + t('successfully')} />, { variant: 'success' });
                            } else {
                                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
                            }
                        }).catch((err: any) => {
                            console.error("===", err);
                            enqueueSnackbar(<AlertMessages text={err?.data?.message || err || "Something went wrong"} />, { variant: 'error' });
                        }).finally(() => setLoading(false));
                    }}
                    submitButtonText="Delete"
                />}
            {statusModal &&
                <FormDialog
                    open={statusModal}
                    onClose={() => setStatusModal(false)}
                    title="Change Status"
                    // icon={<div><Home/></div>}
                    content={
                        <Grid container >
                            <Grid item xs={12}  >
                                <Typography>{t(`Are you sure?`)}</Typography>
                            </Grid>
                        </Grid>
                    }
                    saveFunction={() => {
                        setStatusModal(false);
                        setLoading(true);
                        InspectionFormItemProfileService.ChangeStatusInspectionFormItem((row as any).id, row?.active ? "inactive" : "active", {}).then((response: ChangeStatusInspectionFormItemResponse) => {
                            if (response.success) {
                                refetch();
                                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('status') + ' ' + t('changed') + ' ' + t('successfully')} />, { variant: 'success' });
                            } else {
                                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
                            }
                        }).catch((err: any) => {
                            console.error("===", err);
                            enqueueSnackbar(<AlertMessages text={err?.data?.message || err || "Something went wrong"} />, { variant: 'error' });
                        }).finally(() => setLoading(false));
                    }}
                    submitButtonText="OK"
                />
            }
        </Box>
    )
}